// =============================================================================
// MeuDriver — Cartão de endereços salvos do aluno
// -----------------------------------------------------------------------------
// Lista, cria, edita, define padrão e remove endereços.
// =============================================================================

import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { MapPin, Plus, Star, Pencil, Trash2, Loader2, Home } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useStudentAddresses, type StudentAddress } from '@/hooks/useStudentAddresses'

const addressSchema = z.object({
  label: z
    .string()
    .min(1, 'Informe um rótulo (ex: Casa, Trabalho)')
    .max(40, 'Rótulo muito longo'),
  address: z
    .string()
    .min(5, 'Endereço muito curto')
    .max(240, 'Endereço muito longo'),
})

type FormData = z.infer<typeof addressSchema>

interface AddressFormDialogProps {
  open: boolean
  onClose: () => void
  initial?: StudentAddress | null
  onSubmit: (data: FormData) => Promise<void>
}

function AddressFormDialog({ open, onClose, initial, onSubmit }: AddressFormDialogProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(addressSchema),
    defaultValues: {
      label: initial?.label ?? '',
      address: initial?.address ?? '',
    },
  })

  // Reset form when dialog reopens with different initial
  function handleClose() {
    reset({ label: initial?.label ?? '', address: initial?.address ?? '' })
    onClose()
  }

  async function submit(values: FormData) {
    await onSubmit(values)
    reset({ label: '', address: '' })
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {initial ? 'Editar endereço' : 'Novo endereço'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(submit)} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="label">Rótulo</Label>
            <Input
              id="label"
              placeholder="Casa, Trabalho, Faculdade..."
              {...register('label')}
              className={errors.label ? 'border-red-400' : ''}
            />
            {errors.label && (
              <p className="text-xs text-red-600">{errors.label.message}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="address">Endereço completo</Label>
            <Input
              id="address"
              placeholder="Rua, número, bairro, cidade..."
              {...register('address')}
              className={errors.address ? 'border-red-400' : ''}
            />
            {errors.address && (
              <p className="text-xs text-red-600">{errors.address.message}</p>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting} className="gap-2">
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>{initial ? 'Atualizar' : 'Adicionar'}</>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export function StudentAddressesCard() {
  const { addresses, isLoading, create, update, remove, setDefault } = useStudentAddresses()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<StudentAddress | null>(null)

  function openCreate() {
    setEditing(null)
    setDialogOpen(true)
  }

  function openEdit(addr: StudentAddress) {
    setEditing(addr)
    setDialogOpen(true)
  }

  async function handleSubmit(values: FormData) {
    try {
      if (editing) {
        await update.mutateAsync({ id: editing.id, patch: values })
        toast.success('Endereço atualizado.')
      } else {
        await create.mutateAsync({ ...values, is_default: addresses.length === 0 })
        toast.success('Endereço adicionado.')
      }
      setDialogOpen(false)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao salvar endereço.')
    }
  }

  async function handleSetDefault(addr: StudentAddress) {
    if (addr.is_default) return
    try {
      await setDefault.mutateAsync(addr.id)
      toast.success(`"${addr.label}" definido como padrão.`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao definir padrão.')
    }
  }

  async function handleDelete(addr: StudentAddress) {
    if (!confirm(`Remover "${addr.label}"?`)) return
    try {
      await remove.mutateAsync(addr.id)
      toast.success('Endereço removido.')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao remover.')
    }
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base flex items-center gap-2">
            <MapPin className="h-4 w-4 text-primary" />
            Endereços salvos
          </CardTitle>
          <Button size="sm" variant="outline" onClick={openCreate} className="gap-1">
            <Plus className="h-3.5 w-3.5" /> Adicionar
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : addresses.length === 0 ? (
            <div className="flex flex-col items-center py-8 gap-2 text-center text-muted-foreground">
              <Home className="h-8 w-8 opacity-20" />
              <p className="text-sm">Você ainda não salvou endereços.</p>
              <p className="text-xs opacity-70">
                Salve endereços frequentes para acelerar o agendamento.
              </p>
            </div>
          ) : (
            <ul className="space-y-2">
              {addresses.map((addr) => (
                <li
                  key={addr.id}
                  className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 hover:border-gray-200 transition"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-800 truncate">
                        {addr.label}
                      </p>
                      {addr.is_default && (
                        <span className="text-[10px] font-semibold uppercase bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                          Padrão
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">
                      {addr.address}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {!addr.is_default && (
                      <button
                        onClick={() => handleSetDefault(addr)}
                        className="p-1.5 text-gray-400 hover:text-amber-500 transition"
                        title="Definir como padrão"
                      >
                        <Star className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      onClick={() => openEdit(addr)}
                      className="p-1.5 text-gray-400 hover:text-primary transition"
                      title="Editar"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(addr)}
                      className="p-1.5 text-gray-400 hover:text-red-500 transition"
                      title="Remover"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <AddressFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        initial={editing}
        onSubmit={handleSubmit}
      />
    </>
  )
}
